import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { useTranslations } from 'next-intl';
import { ChangeEventHandler, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { textTrigger } from '@flipster/styled-system/recipes';
import { token } from '@flipster/styled-system/tokens';
import { FormItem, NumberInput } from '@flipster/ui';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { formBunja } from './bunja/form';
import {
  clampPercentToMax,
  clampSpreadToMax,
  maxUserSlippagePercent,
  minUserSlippagePercent,
  type SlippageMode,
} from './bunja/slippage';

const ModeSelect = createDropdownSelect<SlippageMode>();

/**
 * Max slippage control (ERD-233, Figma [web] order 10037:4730). Shown only for perpetual Market orders.
 * - The mode is chosen via a dropdown in the input's suffix (unit `%` / quote ccy + chevron).
 * - By percent: 0.01% ~ 5.00%, sends a tolerance ratio (server computes the limit from BBO).
 * - By price: a SPREAD (offset, quote ccy), clamped to reference·5.00% in real time while typing
 *   ("Auto-capped at max"); at submit the worst price `crossing-side BBO ± spread` is built from the
 *   now-known side, with the spread re-capped to reference·5.00%. (Slack design follow-up.)
 */
const MaxSlippage = () => {
  const t = useTranslations();
  const { isMobile } = useBreakpoint();
  const { symbol, symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision, quoteCurrency } = useAtomValue(symbolInfoWithDefaultAtom);
  const { slippageEnabledAtom, slippageModeAtom, slippageValueAtom, referencePriceAtom } = useBunja(formBunja);

  const store = useStore();
  const [enabled, setEnabled] = useAtom(slippageEnabledAtom);
  const [mode, setMode] = useAtom(slippageModeAtom);
  const [value, setValue] = useAtom(slippageValueAtom);

  const isPercent = mode === 'percent';

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const suppressTooltipRef = useRef(false);

  const showTooltipOnHover = () => {
    if (suppressTooltipRef.current) return;
    setTooltipOpen(true);
  };
  const resetTooltipOnLeave = () => {
    suppressTooltipRef.current = false;
    setTooltipOpen(false);
  };

  const onToggle: ChangeEventHandler<HTMLInputElement> = (e) => {
    const checked = Boolean(e.target.checked);
    setEnabled(checked);
    if (!checked) setValue('');
    suppressTooltipRef.current = true;
    setTooltipOpen(false);
    segment.track(TradeEvent.MarketOrderSlippageClick, { symbol, toggle_on: checked, mode });
  };

  const isModeLocked = value !== '';
  const onChangeMode = (next: SlippageMode) => {
    if (next === mode || isModeLocked) return;
    setMode(next);
  };

  const onChangePercent = (next: string) => setValue(clampPercentToMax(next));
  // 입력 단계 cap은 유지(타이핑 중 reference·5%로 제한); 제출 시 marketOrderLimitPrice는 BBO ± 입력값 raw로 전송.
  // referencePrice는 입력 순간에만 필요하므로 구독하지 않고 store.get으로 읽는다 (매 tick 리렌더 방지).
  const onChangePrice = (next: string) => setValue(clampSpreadToMax(next, store.get(referencePriceAtom)));

  const triggerClasses = textTrigger({ color: 'neutral-4' });

  const unit = isPercent ? '%' : quoteCurrency;
  const modeSuffix = (
    <ModeSelect.Trigger asChild>
      <button
        type='button'
        disabled={isModeLocked}
        className={hstack({ gap: 8, cursor: isModeLocked ? 'not-allowed' : 'pointer', pr: 12 })}
        aria-label={t('Trade.r260611.Select_slippage_type')}
      >
        <p className={css({ textStyle: 'Body/XL/Regular', color: 'content.default.level-4' })}>{unit}</p>
        <ChevronDownIcon size={16} color={token('colors.content.default.level-4')} />
      </button>
    </ModeSelect.Trigger>
  );

  return (
    <div className={css({ py: 8 })}>
      <Tooltip.Root
        placement='top-start'
        maxWidth={280}
        onOpenChange={setTooltipOpen}
        {...(isMobile ? {} : { open: tooltipOpen })}
      >
        <Tooltip.Trigger asChild>
          <div
            className={css({ display: 'inline-flex' })}
            onPointerEnter={isMobile ? undefined : showTooltipOnHover}
            onPointerLeave={isMobile ? undefined : resetTooltipOnLeave}
          >
            <Checkbox checked={enabled} onChange={onToggle}>
              <span className={cx(triggerClasses.root, css({ textStyle: 'Body/XL/Strong' }))}>
                <span className={triggerClasses.label}>
                  <FormattedMessage id='Trade.r260611.Max_slippage' />
                </span>
              </span>
            </Checkbox>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <p>
            <FormattedMessage id='Trade.r260611.The_maximum_slippage_limit_defines_the_worst_execution_price_you_are_willing_to_accept' />
          </p>
          <p className={css({ mt: 8 })}>
            <FormattedMessage id='Trade.r260611.Market_orders_will_be_placed_as_limit_orders_with_time_in_force_ioc' />
          </p>
          <p className={css({ mt: 8 })}>
            <FormattedMessage id='Trade.r260611.Any_portion_order_exceeds_canceled_max_slippage_desc' />
          </p>
        </Tooltip.Content>
      </Tooltip.Root>
      {enabled && (
        <ModeSelect.Root selected={mode} onChange={onChangeMode}>
          <FormItem>
            <NumberInput
              precision={isPercent ? 2 : pricePrecision}
              placeholder={isPercent ? t('Trade.r260611.By_percent') : t('Trade.r260615.By_price')}
              value={value}
              max={isPercent ? String(maxUserSlippagePercent) : undefined}
              onChange={isPercent ? onChangePercent : onChangePrice}
              suffix={modeSuffix}
            />
          </FormItem>
          <ModeSelect.Select>
            <ModeSelect.Item value='percent' label={<FormattedMessage id='Trade.r260611.By_percent_percentage' />} />
            <ModeSelect.Item
              value='price'
              label={<FormattedMessage id='Trade.r260611.By_price_symbol' values={{ symbol: UsdtCurrency }} />}
            />
          </ModeSelect.Select>
          <p
            className={css({
              textStyle: 'Body/S/Regular',
              color: 'content.default.level-3',
            })}
          >
            {isPercent
              ? `${t('Trade.r260611.Range_colon')} ${minUserSlippagePercent.toFixed(2)}% ~ ${maxUserSlippagePercent.toFixed(2)}%`
              : t('Trade.r260611.Auto_capped_at_max')}
          </p>
        </ModeSelect.Root>
      )}
    </div>
  );
};

export default MaxSlippage;
